import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Check, Clock, Copy, Trash2, UserPlus, X } from "react-feather";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/data-table";

export type ProgramAffiliateRow = {
  /**
   * The row's selection key.
   *
   * For "assigned" and "available" this is the affiliate id. For "applicants"
   * it is the APPLICATION id — one affiliate can hold applications to several
   * programs, and each is approved or rejected on its own. `affiliateId` below
   * carries the affiliate for anything that needs the person rather than the
   * decision.
   */
  id: string;
  affiliateId?: string;
  email: string;
  firstName: string;
  lastName: string;
  code: string;
  status: string;
};

const AVATAR_COLORS = [
  "bg-brand-100 text-brand-700",
  "bg-muted text-muted-foreground",
  "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300",
  "bg-brand-100 text-brand-700",
  "bg-muted text-muted-foreground",
  "bg-muted text-foreground",
  "bg-accent text-muted-foreground",
];

function avatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash + name.charCodeAt(i) * (i + 1)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[hash];
}

function initials(firstName: string, lastName: string) {
  return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() || "?";
}

async function copyText(value: string, label: string) {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error(`Failed to copy ${label.toLowerCase()}`);
  }
}

function CopyableCell({
  value,
  label,
  mono = false,
}: {
  value: string;
  label: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const display = value || "—";

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!value) return;
    await copyText(value, label);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="group/copy flex items-center gap-1.5 min-w-0 max-w-full">
      {mono ? (
        <code className="break-all rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
          {display}
        </code>
      ) : (
        <span className="break-all text-sm text-muted-foreground">{display}</span>
      )}
      {value ? (
        <button
          type="button"
          onClick={handleCopy}
          className="shrink-0 rounded p-0.5 text-muted-foreground/60 opacity-0 transition-opacity group-hover/copy:opacity-100 hover:bg-muted hover:text-foreground focus-visible:opacity-100"
          aria-label={`Copy ${label.toLowerCase()}`}
          title={`Copy ${label.toLowerCase()}`}
        >
          {copied ? (
            <Check className="h-3 w-3 text-emerald-500" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </button>
      ) : null}
    </div>
  );
}

type Mode = "assigned" | "available" | "applicants";

type AffiliateRosterTableProps = {
  mode: Mode;
  rows: ProgramAffiliateRow[];
  selectedIds: Set<string>;
  allPageSelected: boolean;
  isSuspended?: boolean;
  isLoading?: boolean;
  pageSize: number;
  addingId?: string | null;
  disabled?: boolean;
  /** Lets the code column explain itself on sneakylink programs. */
  attributionMethod?: "STATIC_CODE" | "SNEAKYLINK";
  onToggleId: (id: string) => void;
  onToggleAllPage: () => void;
  onRemove?: (row: ProgramAffiliateRow) => void;
  onAssign?: (id: string) => void;
  onApprove?: (row: ProgramAffiliateRow) => void;
  onReject?: (row: ProgramAffiliateRow) => void;
};

const EMPTY_TEXT: Record<Mode, string> = {
  assigned: "No affiliates assigned yet",
  available: "No eligible affiliates found",
  applicants:
    "No one is waiting for approval. Affiliates who open this program's share link will appear here.",
};

export function AffiliateRosterTable({
  mode,
  rows,
  selectedIds,
  allPageSelected,
  isSuspended,
  isLoading,
  pageSize,
  addingId,
  disabled,
  attributionMethod,
  onToggleId,
  onToggleAllPage,
  onRemove,
  onAssign,
  onApprove,
  onReject,
}: AffiliateRosterTableProps) {
  const showSkeleton = isLoading && rows.length === 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<ProgramAffiliateRow, any>[]>(
    () => [
      {
        id: "select",
        header: () => (
          <input
            type="checkbox"
            checked={allPageSelected && rows.length > 0}
            onChange={onToggleAllPage}
            disabled={
              isSuspended || disabled || showSkeleton || rows.length === 0
            }
            className="rounded border-border text-brand-600"
            aria-label="Select all on page"
          />
        ),
        cell: ({ row }) => {
          const r = row.original;
          const name = `${r.firstName} ${r.lastName}`.trim();
          return (
            <input
              type="checkbox"
              checked={selectedIds.has(r.id)}
              onChange={() => onToggleId(r.id)}
              disabled={isSuspended || disabled}
              className="rounded border-border text-brand-600"
              aria-label={`Select ${name}`}
            />
          );
        },
        meta: {
          width: "w-10",
          skeleton: <div className="h-4 w-4 rounded bg-muted animate-pulse" />,
        },
      },
      {
        id: "affiliate",
        header: "Affiliate",
        cell: ({ row }) => {
          const r = row.original;
          const name = `${r.firstName} ${r.lastName}`.trim();
          return (
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${avatarColor(name || r.email || String(row.index))}`}
              >
                {initials(r.firstName, r.lastName)}
              </span>
              <p className="break-words font-medium text-foreground">
                {name || "—"}
              </p>
            </div>
          );
        },
        meta: {
          width: "w-44",
          skeleton: (
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-full bg-muted animate-pulse" />
              <div className="h-3.5 w-28 rounded bg-muted animate-pulse" />
            </div>
          ),
        },
      },
      {
        id: "email",
        header: "Email",
        cell: ({ row }) => (
          <CopyableCell value={row.original.email} label="Email" />
        ),
        meta: {
          width: "w-56",
          cellClassName: "pr-3",
          skeleton: <div className="h-3.5 w-36 rounded bg-muted animate-pulse" />,
        },
      },
      {
        id: "code",
        header: mode === "assigned" ? "Discount code" : "Code",
        cell: ({ row }) => {
          const r = row.original;
          return r.code ? (
            <CopyableCell value={r.code} label="Code" mono />
          ) : mode === "applicants" && attributionMethod === "SNEAKYLINK" ? (
            // Sneakylink programs mint a code per click, so an
            // applicant genuinely has none until they're approved.
            <span className="text-xs text-muted-foreground">Sneakylink</span>
          ) : (
            <CopyableCell value="" label="Code" mono />
          );
        },
        meta: {
          width: "w-36",
          skeleton: <div className="h-5 w-20 rounded bg-muted animate-pulse" />,
        },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) =>
          row.original.status === "PENDING" ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
              <Clock className="w-3 h-3" />
              PENDING
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
              <Check className="w-3 h-3" />
              {row.original.status || "APPROVED"}
            </span>
          ),
        meta: {
          width: "w-28",
          skeleton: <div className="h-4 w-14 rounded bg-muted animate-pulse" />,
        },
      },
      {
        id: "spacer",
        header: () => null,
        cell: () => null,
        meta: {
          width: "w-8",
          skeleton: <span aria-hidden="true" />,
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const r = row.original;
          return mode === "applicants" ? (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onApprove?.(r)}
                disabled={isSuspended || disabled}
                className="rounded-md bg-brand-600 p-1.5 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Approve"
                title={
                  isSuspended
                    ? "Program is suspended. Upgrade your plan to approve applicants."
                    : "Approve into this program"
                }
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onReject?.(r)}
                // Rejecting stays available on a suspended program —
                // it doesn't create anything, and the brand still
                // needs a way to clear the queue.
                disabled={disabled}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-600 dark:hover:text-rose-400 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Reject"
                title="Reject applicant"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : mode === "assigned" ? (
            <button
              type="button"
              onClick={() => onRemove?.(r)}
              disabled={isSuspended || disabled}
              className="rounded-md p-1 text-muted-foreground hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-600 dark:hover:text-rose-400 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Remove"
              title={
                isSuspended ? "Program is suspended" : "Remove from program"
              }
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onAssign?.(r.id)}
              disabled={isSuspended || disabled || addingId === r.id}
              className="inline-flex items-center justify-center rounded-lg bg-brand-600 p-1.5 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={addingId === r.id ? "Adding…" : "Assign"}
              title={
                isSuspended
                  ? "Program is suspended. Upgrade your plan to assign affiliates."
                  : addingId === r.id
                    ? "Adding…"
                    : "Assign to this program"
              }
            >
              {addingId === r.id ? (
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <UserPlus className="w-3.5 h-3.5" />
              )}
            </button>
          );
        },
        meta: {
          width: "w-28",
          headerClassName: "text-left",
          cellClassName: "text-left",
          skeleton: <div className="h-6 w-16 rounded bg-muted animate-pulse" />,
        },
      },
    ],
    [
      mode,
      rows.length,
      selectedIds,
      allPageSelected,
      isSuspended,
      disabled,
      showSkeleton,
      addingId,
      attributionMethod,
      onToggleId,
      onToggleAllPage,
      onRemove,
      onAssign,
      onApprove,
      onReject,
    ],
  );

  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={isLoading}
      skeletonRows={Math.min(pageSize, 10)}
      emptyText={EMPTY_TEXT[mode]}
      getRowId={(row) => row.id}
    />
  );
}
